import { useAssignGuardian } from 'common/queries-and-mutations/guardian';
import Button from 'components/Form/Button';
import CustomSelect from 'components/Form/CustomSelect';
import { Icon, Icons } from 'components/Icon';
import SuccessModal from 'components/Modal/SuccessModal';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { Guardians } from 'service/dto/guardian.dto';
import * as Yup from 'yup';
import { InfoModal } from 'components/Modal';
import withCreatePortal from 'components/HOC/withCreatePortal';

type Props = {
  onClose: () => void;
  onAddGuardianClick: () => void;
  onAssignGuardianComplete: () => void;
  guardians: Guardians;
  childrenId: number | null;
};

const EnhancedSuccessModal = withCreatePortal(SuccessModal);

export default function AssignGuardianModal({
  onClose,
  onAssignGuardianComplete,
  guardians,
  childrenId,
  onAddGuardianClick
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { mutate, isSuccess, isError, isLoading } = useAssignGuardian();

  const formik = useFormik({
    initialValues: {
      guardian: null
    },
    validationSchema: Yup.object({
      guardian: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string()
        })
        .nullable()
        .required('Please select a guardian')
    }),
    onSubmit: (values, { setFieldTouched }) => {
      for (const key in values) {
        setFieldTouched(key, true);
      }
      assignGuardian(values);
    },
    enableReinitialize: true
  });

  const assignGuardian = (values: any) => {
    setIsSubmitting(true);
    mutate({ guardianId: values.guardian.value, childrenId: childrenId! });
  };

  const formatGuardians = useMemo(() => {
    return guardians.map(guardian => ({
      value: guardian.id!,
      label: `${guardian.guardianFirstName} ${guardian.guardianMiddleName} ${guardian.guardianLastName} (${guardian.guardianType})`
    }));
  }, [guardians]);

  const onProccessClick = () => {
    setIsSubmitting(false);
    onAssignGuardianComplete();
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setShowSuccess(true);
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <>
      {showSuccess ? (
        <EnhancedSuccessModal
          title="Guardian Successfully Assigned"
          onProccessClick={() => {
            onProccessClick();
            onClose();
          }}
          className="w-[20%]"
        />
      ) : (
        <InfoModal
          width="w-[55%]"
          className="h-fit rounded-md px-4 pb-9 pt-5 sm:p-9"
        >
          <div>
            <div className="flex flex-row items-center justify-end pl-6">
              <button type="button" onClick={onClose}>
                <Icon name={Icons.Close} />
              </button>
            </div>
            <div className="flex flex-row items-center pl-4">
              <p className="text-lg font-bold text-[#353535]">
                Your child is under 18, please assign a guardian.
              </p>
            </div>
            <div className="flex flex-col items-center justify-center">
              <form
                autoComplete="false"
                className="mt-10 flex w-full flex-col items-center justify-center px-6 md:w-[60%]"
                onSubmit={formik.handleSubmit}
              >
                <span className="flex">
                  <label className="mb-2 text-[12px] font-medium text-[#353535]">
                    Select existing guardian
                  </label>
                </span>
                <CustomSelect
                  options={formatGuardians}
                  selectedOption={formik.values.guardian}
                  handleOptionChange={option => {
                    formik.setFieldValue(`guardian`, option);
                  }}
                  name="guardian"
                  isSearchable
                  className="h-[3rem] rounded-xl"
                  error={formik.touched.guardian ? formik.errors.guardian : ''}
                />
                <div className="flex justify-center">
                  <Button
                    variant="primary"
                    size="md"
                    className={`col-start-2 col-end-2 my-5 mt-8 w-[10rem] rounded p-[0.2rem]`}
                    label="Assign"
                    type="submit"
                    disabled={!formik.isValid}
                    loading={isSubmitting || isLoading}
                  ></Button>
                </div>
              </form>
              <button
                type="button"
                onClick={onAddGuardianClick}
                className="my-10 text-lg font-bold text-primary-900 underline"
              >
                Add New Guardian
              </button>
            </div>
          </div>
        </InfoModal>
      )}
    </>
  );
}
